import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Input, PasswordInput } from '../../components/ui/Input';
import { Card } from '../../components/ui/Card';
import { ErrorBanner } from '../../components/ui/ErrorBanner';
import { useAuth } from '../../hooks/useAuth';
import { extractErrorMessage } from '../../api/client';
import { loginSchema, type LoginInput } from '../../lib/validators';
import { useState } from 'react';

export function LoginPage() {
  const { login } = useAuth();
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    mode: 'onChange',
  });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await login(values);
    } catch (error) {
      setFormError(extractErrorMessage(error, 'Не удалось войти'));
    }
  });

  return (
    <Card className="flex flex-col gap-4 p-6">
      <div>
        <h1 className="text-2xl font-semibold text-text">Вход в OnlyRead</h1>
        <p className="mt-1 text-sm text-text-muted">
          Распознавайте текст и работайте с PDF.
        </p>
      </div>

      {formError && <ErrorBanner message={formError} />}

      <form className="flex flex-col gap-3" onSubmit={onSubmit} noValidate>
        <Input
          label="Email"
          type="email"
          autoComplete="email"
          autoFocus
          placeholder="you@example.ru"
          error={errors.email?.message}
          {...register('email')}
        />
        <PasswordInput
          label="Пароль"
          autoComplete="current-password"
          placeholder="••••••••"
          error={errors.password?.message}
          {...register('password')}
        />
        <div className="flex justify-end">
          <Link
            to="/restore"
            className="text-sm font-medium text-brand-primary hover:underline"
          >
            Забыли пароль?
          </Link>
        </div>
        <Button
          type="submit"
          fullWidth
          loading={isSubmitting}
          disabled={!isValid}
          size="lg"
        >
          Войти
        </Button>
      </form>

      <p className="text-center text-sm text-text-muted">
        Ещё нет аккаунта?{' '}
        <Link to="/signup" className="font-medium text-brand-primary hover:underline">
          Создать
        </Link>
      </p>
    </Card>
  );
}
